/**
 * The app's shared "this did not work, and here is why" modal (BON-472).
 *
 * IT RENDERS THE MESSAGE IT IS GIVEN AND NOTHING ELSE — no stack trace, no
 * status code, no error name. The messages it carries are written for a CSR by
 * the API that refused the request; appending machine detail turns a sentence
 * they can act on into one they forward to engineering.
 *
 * A dialog rather than a toast because every case that reaches it needs a
 * decision: wait for a payment to finish, release the seats, reload the row.
 * Focus lands on the acknowledge button, so it cannot be missed.
 *
 * THE MESSAGE IS THE DIALOG'S DESCRIPTION, not just text inside it. Labelled
 * alone, a screen reader announces the title and stops — and the title is the
 * part that is deliberately short. The decision the CSR has to make is in the
 * sentence underneath.
 */
import { AlertTriangle, XCircle } from 'lucide-react';
import { useId, useRef } from 'react';

import { useDialogKeyboard } from '../hooks/useDialogKeyboard';

export type ErrorDialogTone = 'error' | 'warning';

const TONE = {
  error: {
    Icon: XCircle,
    iconClass: 'text-red-600 dark:text-red-400',
    panelClass: 'bg-red-50 dark:bg-red-900/20',
  },
  warning: {
    Icon: AlertTriangle,
    iconClass: 'text-clay-600 dark:text-clay-400',
    panelClass: 'bg-clay-50 dark:bg-clay-900/20',
  },
} as const;

export function ErrorDialog({
  title,
  message,
  tone = 'error',
  actionLabel = 'OK',
  onClose,
}: {
  title: string;
  /** Shown verbatim. Give it the API's own words. */
  message: string;
  /** `warning` for something that half-happened and needs following up;
   *  `error` for a request that was refused outright. */
  tone?: ErrorDialogTone;
  actionLabel?: string;
  onClose: () => void;
}) {
  const { Icon, iconClass, panelClass } = TONE[tone];
  // Generated rather than fixed: an outcome dialog can be on screen at the same
  // time as the form that caused it, and duplicate ids would point every
  // description at whichever rendered first.
  const id = useId();
  const acknowledgeRef = useRef<HTMLButtonElement>(null);
  useDialogKeyboard(onClose, acknowledgeRef);

  return (
    <div
      role="presentation"
      onClick={onClose}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-message`}
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-[#1a1f1c]"
      >
        <div className={`flex items-start gap-3 px-6 py-5 ${panelClass}`}>
          <Icon size={22} className={`mt-0.5 flex-shrink-0 ${iconClass}`} aria-hidden />
          <div>
            <h2
              id={`${id}-title`}
              className="text-base font-bold text-bark-900 dark:text-stone-100"
            >
              {title}
            </h2>
            <p id={`${id}-message`} className="mt-1 text-sm text-stone-700 dark:text-stone-300">
              {message}
            </p>
          </div>
        </div>
        <div className="flex justify-end border-t border-stone-200 px-6 py-4 dark:border-stone-700">
          <button
            type="button"
            ref={acknowledgeRef}
            onClick={onClose}
            className="rounded-lg bg-stone-800 px-4 py-2 text-sm font-semibold text-white hover:bg-stone-700 dark:bg-stone-200 dark:text-stone-900 dark:hover:bg-white"
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
